import React, { useMemo, useCallback } from 'react';
import { CircularProgress, Tooltip, Box, Typography } from '@mui/material';
import {
  VisibilityOutlined as ViewIcon,
  EditOutlined as EditIcon,
  CancelOutlined as CancelIcon,
  PrintOutlined as PrintIcon,
  ReceiptOutlined as ReceiptIcon,
} from '@mui/icons-material';
import { InventoryRecord } from '../../../types/inventory.types';
import { BaseTable, TableColumn } from '../../common/Table';
import { StatusChip, ActionButton } from '../../common/Table/Table.styles';
import { mapInventoryRecordStatus } from '../../common/status';
import EmptyStateMessage from '../../common/EmptyStateMessage';

export interface InventoryTableProps {
  records: InventoryRecord[];
  loading: boolean;
  selectedRecords: number[];
  setSelectedRecords: React.Dispatch<React.SetStateAction<number[]>>;
  onViewRecord?: (id: number) => void;
  onEditRecord?: (id: number) => void;
  onCancelRecord?: (id: number) => void;
  onPrintRecord?: (id: number) => void;
  onPrintLabels?: (id: number) => void;
  sortBy?: 'createdAt' | 'recordCompletedAt' | 'recordCode';
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (field: 'createdAt' | 'recordCompletedAt' | 'recordCode') => void;
}

const InventoryTable: React.FC<InventoryTableProps> = ({
  records,
  loading,
  selectedRecords,
  setSelectedRecords,
  onViewRecord,
  onEditRecord,
  onCancelRecord,
  onPrintRecord,
  onPrintLabels,
  sortBy,
  sortOrder,
  onSortChange,
}) => {
  const isCompleted = useCallback((status: string | undefined) => {
    const s = (status || '').toLowerCase();
    return s === 'completado' || s === 'completed';
  }, []);

  const formatDate = useCallback((date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const columns: TableColumn<InventoryRecord>[] = useMemo(
    () => [
      {
        id: 'id',
        label: 'Número de Registro',
        sortField: 'recordCode',
        render: (record) => (
          <Typography color="text.primary" fontWeight={600} fontSize="0.95rem">
            {record.recordCode != null && record.recordCode !== '' ? record.recordCode : '—'}
          </Typography>
        ),
      },
      {
        id: 'supplier',
        label: 'Proveedor',
        render: (record) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {record.supplier?.name?.trim() ? record.supplier.name : '—'}
          </Typography>
        ),
      },
      {
        id: 'createdAt',
        label: 'Fecha de creación',
        sortField: 'createdAt',
        render: (record) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {record.createdAt ? formatDate(record.createdAt) : '—'}
          </Typography>
        ),
      },
      {
        id: 'registrationDate',
        label: 'Fecha de registro',
        sortField: 'recordCompletedAt',
        render: (record) => {
          const iso = isCompleted(record.status) ? record.completedAt : undefined;
          return (
            <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
              {iso ? formatDate(iso) : '—'}
            </Typography>
          );
        },
      },
      {
        id: 'distinctProductsCount',
        label: 'Cantidad de productos',
        align: 'center',
        render: (record) => (
          <Typography color="text.primary" fontWeight={600} fontSize="0.95rem">
            {record.distinctProductsCount ?? 0}
          </Typography>
        ),
      },
      {
        id: 'totalQuantity',
        label: 'Cantidad de piezas',
        align: 'center',
        render: (record) => (
          <Typography color="text.primary" fontWeight={600} fontSize="0.95rem">
            {record.totalItemsCount ?? (record as any).totalQuantity ?? '—'}
          </Typography>
        ),
      },
      {
        id: 'status',
        label: 'Estado',
        align: 'center',
        render: (record) =>
          (() => {
            const chip = mapInventoryRecordStatus(record.status || '');
            return <StatusChip label={chip.label} size="small" color={chip.color} />;
          })(),
      },
    ],
    [formatDate, isCompleted],
  );

  const handleSelectItems = useCallback(
    (items: (string | number)[]) => setSelectedRecords(items as number[]),
    [setSelectedRecords],
  );

  const handleRequestSort = useCallback(
    (field: string) => {
      if (onSortChange == null) {
        return;
      }
      if (field === 'createdAt' || field === 'recordCompletedAt' || field === 'recordCode') {
        onSortChange(field);
      }
    },
    [onSortChange],
  );

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Render empty state
  if (!records || records.length === 0) {
    return (
      <EmptyStateMessage message="No hay registros de inventario para mostrar." minHeight={200} />
    );
  }

  return (
    <BaseTable<InventoryRecord>
      items={records}
      columns={columns}
      selectedItems={selectedRecords}
      onSelectItems={handleSelectItems}
      showCheckbox
      showActions
      getItemId={(record) => record.id}
      emptyStateTitle="No hay registros de inventario para mostrar."
      emptyStateSubtitle="Ajuste los filtros o añada un nuevo registro."
      sortBy={sortBy}
      sortOrder={sortOrder}
      onRequestSort={onSortChange != null ? handleRequestSort : undefined}
      renderActions={(record) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {onViewRecord && (
            <Tooltip title="Ver registro">
              <ActionButton size="small" onClick={() => onViewRecord!(record.id)}>
                <ViewIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          )}
          {onEditRecord && (
            <Tooltip title="Editar">
              <ActionButton size="small" onClick={() => onEditRecord!(record.id)}>
                <EditIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          )}
          {onCancelRecord && (
            <Tooltip title="Cancelar">
              <ActionButton size="small" onClick={() => onCancelRecord!(record.id)}>
                <CancelIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          )}
          {onPrintRecord && (
            <Tooltip title="Imprimir detalle del registro">
              <ActionButton size="small" onClick={() => onPrintRecord(record.id)}>
                <PrintIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          )}
          {onPrintLabels && isCompleted(record.status) && (
            <Tooltip title="Imprimir etiquetas de piezas">
              <ActionButton size="small" onClick={() => onPrintLabels(record.id)}>
                <ReceiptIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          )}
        </Box>
      )}
    />
  );
};

export default InventoryTable;
